import logger from './logger';

/**
 * Error monitoring.
 *
 * The app previously had no crash reporting at all: render errors were swallowed by a
 * fake error boundary and a `window.onerror` handler silently reloaded the page on two
 * specific minified messages, so real production failures were invisible.
 *
 * Sentry is loaded lazily and only when REACT_APP_SENTRY_DSN is set. That keeps it out of
 * the main bundle entirely for anyone who hasn't configured a DSN — nothing to install or
 * disable, it simply stays inert.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENV || 'development';

let sentry = null;
let initPromise = null;

/** True when a DSN is configured; callers can use this to skip reporting work. */
export const isMonitoringEnabled = () => Boolean(DSN);

/**
 * Initialize monitoring. Safe to call unconditionally — it resolves to null and does
 * nothing when no DSN is configured. Errors during init never propagate: monitoring must
 * not be able to take the app down.
 */
export const initMonitoring = () => {
    if (!DSN) {
        logger.log('Monitoring disabled (no REACT_APP_SENTRY_DSN)');
        return Promise.resolve(null);
    }
    if (initPromise) return initPromise;

    initPromise = import('@sentry/react')
        .then((Sentry) => {
            Sentry.init({
                dsn: DSN,
                environment: ENVIRONMENT,
                // Conservative defaults; raise deliberately once volume is understood.
                tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
                sendDefaultPii: false,
            });
            sentry = Sentry;
            logger.log('Monitoring initialized', ENVIRONMENT);
            return Sentry;
        })
        .catch((error) => {
            logger.log('Monitoring failed to initialize', error?.message);
            return null;
        });

    return initPromise;
};

/**
 * Report an error. No-ops when monitoring is disabled, so call sites don't need guards.
 * `context` is attached as extra data (keep it free of PII).
 */
export const captureError = (error, context = {}) => {
    logger.log('captureError', error?.message, context);
    if (!DSN) return;

    const send = (Sentry) => {
        if (!Sentry) return;
        Sentry.withScope((scope) => {
            Object.entries(context).forEach(([key, value]) => scope.setExtra(key, value));
            Sentry.captureException(error);
        });
    };

    if (sentry) {
        send(sentry);
    } else {
        // Init still in flight (or not started) — chain onto it so nothing is dropped.
        initMonitoring().then(send);
    }
};
